import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import ProviderUtils from "../utils/provider.utils";

type PolicyPdfDialogProps = {
  open: boolean;
  onClose: () => void;
  company: string;
};

const SUPPORTED_LANGUAGES = ["ru", "en", "hy", "ar", "tr"];

const AVAILABLE_POLICY_LANGUAGES: Record<string, string[]> = {
  am: ["ru", "en", "hy"],
  by: ["ru"],
  ae: ["en", "ar"],
  хх: ["ru"],
};

const normalizeLanguage = (language?: string) => {
  const code = (language || "ru").split("-")[0];

  return SUPPORTED_LANGUAGES.includes(code) ? code : "ru";
};

const getPolicyLanguage = (provider: string, language: string) => {
  if (provider === "ae") {
    return "en";
  }

  const availableLanguages = AVAILABLE_POLICY_LANGUAGES[provider] || ["ru"];
  const fallbackLanguage = availableLanguages[0] || "ru";

  return availableLanguages.includes(language) ? language : fallbackLanguage;
};

const buildPolicyPdfUrl = (provider: string, language: string) => {
  return `/documents/policies/${provider}/privacy-policy-${language}.pdf`;
};

const PolicyPdfDialog = ({ open, onClose, company }: PolicyPdfDialogProps) => {
  const { t, i18n } = useTranslation();
  const [pdfUrl, setPdfUrl] = useState("");

  useEffect(() => {
    if (!open) return;

    const provider = ProviderUtils.getProvider();
    const language = normalizeLanguage(i18n.language);
    const policyLanguage = getPolicyLanguage(provider, language);

    setPdfUrl(buildPolicyPdfUrl(provider, policyLanguage));
  }, [open, i18n.language]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
      <DialogTitle>{t("footer.privacyPolicyTitle", { company })}</DialogTitle>

      <DialogContent sx={{ height: "85vh", p: 0 }}>
        {pdfUrl && (
          <iframe
            src={pdfUrl}
            title={t("footer.privacyPolicy")}
            width="100%"
            height="100%"
            style={{ border: "none" }}
          />
        )}
      </DialogContent>

      <DialogActions>
        <Button
          component="a"
          href={pdfUrl}
          target="_blank"
          rel="noreferrer"
          disabled={!pdfUrl}
        >
          {t("footer.openInNewTab")}
        </Button>

        <Button onClick={onClose}>{t("footer.close")}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PolicyPdfDialog;
